import * as React from "react";
import { cn } from "../lib/cn.js";

/**
 * The EduViaOS mark: an open book seen end-on, its two pages forming the
 * "V" of Via, with a guiding star rising from the spine. Ported from
 * `eduviaos-landing/src/components/site/logo.tsx` so every portal wears
 * the same identity as the marketing site.
 *
 * Colours come from `--eduviaos-ink` / `--eduviaos-teal` /
 * `--eduviaos-saffron` (defined in `@eduviaos/design-tokens/tokens.css`,
 * with dark-mode values), falling back to the landing palette so the mark
 * still renders correctly where the tokens aren't loaded. `variant="mono"`
 * paints everything in `currentColor` for use on coloured tiles.
 */

export const EDUVIAOS_MARK_PATHS = {
  ink: "M11 19C14.5 33 21 43.5 30.5 50",
  teal: "M53 19C49.5 33 43 43.5 33.5 50",
  star: "M32 11.5C32.9 17.3 34.7 19.1 40.5 20C34.7 20.9 32.9 22.7 32 28.5C31.1 22.7 29.3 20.9 23.5 20C29.3 19.1 31.1 17.3 32 11.5Z",
} as const;

const BRAND_COLOURS = {
  ink: "var(--eduviaos-ink, #16213a)",
  teal: "var(--eduviaos-teal, #1b7f7c)",
  saffron: "var(--eduviaos-saffron, #e8892b)",
  saffronInk: "var(--eduviaos-saffron-ink, #b8661a)",
} as const;

export interface EduViaOSMarkProps extends Omit<React.SVGProps<SVGSVGElement>, "children"> {
  /** Accessible name. Omit for a decorative mark (it is then aria-hidden). */
  title?: string;
  /** `brand` (default) uses the brand palette; `mono` uses currentColor. */
  variant?: "brand" | "mono";
}

export function EduViaOSMark({ title, variant = "brand", className, ...props }: EduViaOSMarkProps) {
  const mono = variant === "mono";
  const titleId = React.useId();
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-labelledby={title ? titleId : undefined}
      className={cn("shrink-0", className)}
      {...props}
    >
      {title ? <title id={titleId}>{title}</title> : null}
      <path
        d={EDUVIAOS_MARK_PATHS.ink}
        stroke={mono ? "currentColor" : BRAND_COLOURS.ink}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <path
        d={EDUVIAOS_MARK_PATHS.teal}
        stroke={mono ? "currentColor" : BRAND_COLOURS.teal}
        strokeOpacity={mono ? 0.75 : undefined}
        strokeWidth="8"
        strokeLinecap="round"
      />
      <path d={EDUVIAOS_MARK_PATHS.star} fill={mono ? "currentColor" : BRAND_COLOURS.saffron} />
    </svg>
  );
}

export interface EduViaOSWordmarkProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Paint "Via" in currentColor instead of the brand saffron. */
  mono?: boolean;
}

export function EduViaOSWordmark({ className, mono = false, ...props }: EduViaOSWordmarkProps) {
  return (
    <span className={cn("font-semibold tracking-[-0.02em]", className)} {...props}>
      Edu
      <span
        className="font-medium italic"
        style={mono ? undefined : { color: BRAND_COLOURS.saffronInk }}
      >
        Via
      </span>
      OS
    </span>
  );
}

export interface EduViaOSLogoProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Mark size in px. The wordmark scales with it. Default 28. */
  size?: number;
  /** Hide the wordmark (e.g. in a collapsed sidebar). */
  markOnly?: boolean;
  variant?: "brand" | "mono";
}

/**
 * Mark + wordmark lockup. Reads as "EduViaOS" to assistive tech even when
 * `markOnly` is set.
 */
export function EduViaOSLogo({
  size = 28,
  markOnly = false,
  variant = "brand",
  className,
  ...props
}: EduViaOSLogoProps) {
  return (
    <span
      className={cn("inline-flex items-center gap-2 leading-none", className)}
      {...(markOnly ? { role: "img", "aria-label": "EduViaOS" } : {})}
      {...props}
    >
      <EduViaOSMark variant={variant} style={{ width: size, height: size }} />
      {markOnly ? null : (
        <EduViaOSWordmark mono={variant === "mono"} style={{ fontSize: Math.round(size * 0.68) }} />
      )}
    </span>
  );
}
